import type { AdminAuditEvent } from '@/admin/adminApi';
import { totalIsKnown, pageCount, type PageState } from '@/admin/users/accountPresentation';

/**
 * Mise en forme du journal d'audit, ecrite **hors des composants**.
 *
 * Regle de l'ecran : **aucun identifiant technique en valeur principale**. Une
 * ligne dit « qui » par une adresse (ou « Console »), « quoi » par une phrase
 * francaise, « sur qui » par une adresse ou un libelle ; l'UUID reste au second
 * rang, copiable, parce que c'est lui qu'on colle dans une commande console.
 *
 * Aucun nombre n'est calcule ici : le total vient du serveur.
 */

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * Actions que le serveur ecrit dans `operational_audit_events`.
 *
 * **La liste est verifiee, pas affirmee** : `auditActionsCoverage.test.ts` relit
 * les appels `recordAudit(...)` du backend et echoue des qu'une action — ou un
 * type de cible — n'a pas de libelle ici, ou qu'un libelle ne correspond plus a
 * rien. Les familles sont `admin.*` (acces et actions d'administration),
 * `privacy.*` (droits exerces par le titulaire d'un compte) et
 * `synchronization.*` (commandes de reprise, jouees en console).
 *
 * **Une action inconnue s'affiche brute** (`actionLabel`), jamais masquee ni
 * traduite en « autre » : si le serveur en gagne une, l'ecran la montre telle
 * qu'elle est ecrite, et c'est aussi ce qui garde lisibles les lignes d'une
 * version plus ancienne ou plus recente.
 */
export const ACTION_LABELS: Readonly<Record<string, string>> = {
  'admin.access': 'Consultation',
  'admin.sync.recovery_requested': 'Récupération de synchronisation demandée',
  'synchronization.recovery_updated': 'Récupération de synchronisation mise à jour',
  'admin.reauthentication_failed': 'Mot de passe refusé (action sensible)',
  'admin.user.sessions_revoked': 'Sessions révoquées',
  'admin.user.anonymized': 'Compte anonymisé',
  'admin.user.suspended': 'Compte suspendu',
  'admin.user.reactivated': 'Compte réactivé',
  'admin.user.export_requested': 'Export de compte demandé',
  'admin.user.export_downloaded': 'Export de compte téléchargé',
  'admin.privacy_request.created': 'Demande RGPD enregistrée',
  'admin.privacy_request.updated': 'Demande RGPD mise à jour',
  'admin.privacy.exports_purged': 'Exports expirés purgés',
  'admin.setting.updated': 'Réglage modifié',
  'admin.failed_message.retried': 'Traitement rejoué',
  'admin.legal_document.updated': 'Document légal modifié (avant les versions)',
  'admin.legal_document.draft_saved': 'Brouillon de document légal enregistré',
  'admin.legal_document.published': 'Document légal publié',
  'admin.role.granted': 'Rôle attribué',
  'admin.role.revoked': 'Rôle retiré',
  'admin.audit.exported': 'Journal exporté',
  'privacy.consent_updated': 'Consentement modifié',
  'privacy.devices_revoked': 'Appareils déconnectés par le titulaire',
  'privacy.account_deleted': 'Compte supprimé par le titulaire',
  'family_sharing.account_removed': 'Compte retiré du partage familial (suppression ou nettoyage)',
  'synchronization.baby_backfilled': 'Profil bébé repris dans la synchronisation',
  'synchronization.baby_registration_reverted': 'Reprise d’un profil bébé annulée',
  'synchronization.baby_profile_reconciled': 'Profil bébé remis à niveau avec la synchronisation',
};

export const actionLabel = (action: string): string => ACTION_LABELS[action] ?? action;

/**
 * Actions que le backend n'ecrit **plus** mais dont des lignes peuvent subsister dans
 * le journal (il ne se purge pas : ADR 0011). Leur libelle est garde, et seulement
 * ceux-la echappent au test qui refuse un libelle sans appel correspondant.
 *
 * - `admin.legal_document.updated` : ecrit par l'ancien `PATCH` des documents legaux
 *   (#494) jusqu'a BO-12, qui l'a remplace par `…draft_saved` et `…published`.
 */
export const HISTORICAL_ACTIONS: readonly string[] = ['admin.legal_document.updated'];

/** Vrai quand l'action est dans le dictionnaire (sinon l'ecran montre le code brut). */
export const isKnownAction = (action: string): boolean => action in ACTION_LABELS;

/** Options du filtre « Action », par ordre alphabetique du libelle francais. */
export const ACTION_OPTIONS: readonly { readonly value: string; readonly label: string }[] = Object.entries(ACTION_LABELS)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

// ---------------------------------------------------------------------------
// Types de cible
// ---------------------------------------------------------------------------

export const TARGET_TYPE_LABELS: Readonly<Record<string, string>> = {
  identity_user: 'Compte',
  synchronization_recovery: 'Récupération de synchronisation',
  route: 'Consultation d’une page',
  failed_message: 'Message en échec',
  application_setting: 'Réglage',
  legal_document: 'Document légal',
  data_export_file: 'Fichier d’export',
  privacy_request: 'Demande RGPD',
  security_consent: 'Consentement',
  baby_profile: 'Profil bébé',
  sensitive_action: 'Action sensible',
};

export const targetTypeLabel = (type: string): string => TARGET_TYPE_LABELS[type] ?? type;

/** Options du filtre « Type de cible ». */
export const TARGET_TYPE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = Object.entries(
  TARGET_TYPE_LABELS,
).map(([value, label]) => ({ value, label }));

// ---------------------------------------------------------------------------
// Acteur et cible
// ---------------------------------------------------------------------------

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/giu;

/** Un UUID en entier ne se lit pas : ses quatre premiers caracteres suffisent a le reconnaitre. */
const shortenUuids = (text: string): string => text.replace(UUID_PATTERN, (uuid) => `${uuid.slice(0, 4)}…`);

/**
 * Debut d'un identifiant, pour le second rang d'une cellule de tableau. Seul un
 * identifiant long en hexadecimal (UUID, jeton de correlation) est abrege : un
 * mot (`revoke_sessions`, la nature d'une action sensible) se lit en entier.
 */
export const shortId = (id: string): string =>
  id.length > 16 && /^[0-9a-f-]+$/iu.test(id) ? `${id.slice(0, 8)}…` : id;

export interface PartyLabel {
  /** Ce qu'on lit en premier ; `null` : rien a nommer (affiche « — »). */
  readonly label: string | null;
  /** Identifiant ou precision, au second rang. */
  readonly secondary: string | null;
}

/**
 * Qui a agi. Un acteur nul est la **console** : une commande jouee sur le
 * serveur, sans session applicative — une information reelle, pas un trou.
 * Un acteur sans adresse est un compte que la lecture n'a pas retrouve : on le
 * dit, au lieu d'afficher son UUID.
 */
export const describeActor = (event: AdminAuditEvent): PartyLabel => {
  if (event.actorUserId === null) {
    return { label: 'Console', secondary: null };
  }

  if (event.actorEmail !== null && event.actorEmail !== '') {
    return { label: event.actorEmail, secondary: shortId(event.actorUserId) };
  }

  return { label: 'Compte introuvable', secondary: shortId(event.actorUserId) };
};

/** Chemin d'API rendu lisible : sans `/api/v1`, avec la methode, UUID abreges. */
const describeRoute = (path: string, method: unknown): string => {
  const readable = shortenUuids(path.replace(/^\/api\/v1/u, ''));

  return typeof method === 'string' && method !== '' ? `${method} ${readable}` : readable;
};

/**
 * Sur quoi l'action portait. L'adresse d'un compte est lue a la reponse
 * (`targetEmail`), jamais ecrite dans le journal : un compte anonymise y
 * apparait avec son adresse anonymisee.
 */
export const describeTarget = (event: AdminAuditEvent): PartyLabel => {
  const { targetType, targetId } = event;

  if (targetType === null || targetId === null) {
    return { label: null, secondary: null };
  }

  if (event.targetEmail !== null && event.targetEmail !== '') {
    return { label: event.targetEmail, secondary: shortId(targetId) };
  }

  switch (targetType) {
    case 'route':
      return { label: describeRoute(targetId, event.metadata.method), secondary: null };
    case 'application_setting':
      return { label: targetId, secondary: targetTypeLabel(targetType) };
    case 'legal_document':
      return { label: targetId.toLocaleUpperCase('fr-FR'), secondary: targetTypeLabel(targetType) };
    default:
      return { label: targetTypeLabel(targetType), secondary: shortId(targetId) };
  }
};

// ---------------------------------------------------------------------------
// Metadonnees
// ---------------------------------------------------------------------------

/** Motif saisi par l'administrateur, lu de `metadata.reason` ; `null` si absent ou vide. */
export const reasonOf = (metadata: Readonly<Record<string, unknown>>): string | null => {
  const reason = metadata.reason;

  return typeof reason === 'string' && reason.trim() !== '' ? reason.trim() : null;
};

/** Noms des parametres que la garde conserve (`AdminAccessGuard::KEPT_QUERY_PARAMETERS`), dits en francais. */
const QUERY_PARAMETER_LABELS: Readonly<Record<string, string>> = {
  page: 'Page',
  perPage: 'Taille de page',
  days: 'Nombre de jours',
  period: 'Période',
  state: 'État',
  role: 'Rôle',
  action: 'Action',
  excludeAction: 'Action écartée',
  targetType: 'Type de cible',
  type: 'Type',
  userId: 'Compte',
  actorUserId: 'Acteur',
  entityId: 'Entité',
  from: 'Depuis',
  to: 'Jusqu’à',
  updatedFrom: 'Modifié depuis',
  updatedTo: 'Modifié jusqu’à',
  targetId: 'Cible',
  correlationId: 'Corrélation',
  search: 'Recherche',
  q: 'Recherche',
};

export interface ConsultedParameter {
  readonly label: string;
  readonly value: string;
}

export type ConsultedQuery =
  /** Aucun parametre : la requete n'en portait pas. */
  | { readonly kind: 'none' }
  /** Objet ecrit par la garde depuis #561. */
  | { readonly kind: 'parameters'; readonly parameters: readonly ConsultedParameter[] }
  /** Ligne anterieure : la chaine de requete brute, telle qu'ecrite. */
  | { readonly kind: 'raw'; readonly text: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const describeParameterValue = (value: unknown): string => {
  // Un terme libre n'est jamais ecrit : seule sa presence l'est (`search`, `q`),
  // ou celle d'un parametre dont la valeur n'avait pas la forme attendue.
  if (isRecord(value) && value.present === true) {
    return typeof value.length === 'number'
      ? `renseigné, non conservé (${String(value.length)} caractère${value.length > 1 ? 's' : ''})`
      : 'renseigné, non conservé';
  }

  return typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value);
};

/**
 * Parametres d'une consultation (`metadata.query` d'une ligne `admin.access`).
 *
 * Depuis #561 la garde n'ecrit plus la chaine de requete brute : c'est une
 * **chaine JSON** d'objet — valeurs conservees quand elles ont leur forme,
 * `{present, length}` pour un terme libre, `unknownParameters` pour le reste.
 * Les lignes d'avant gardent leur texte brut, qu'on montre tel quel plutot que
 * de le deviner.
 */
export const parseConsultedQuery = (metadata: Readonly<Record<string, unknown>>): ConsultedQuery => {
  const query = metadata.query;

  if (typeof query !== 'string' || query.trim() === '') {
    return { kind: 'none' };
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(query);
  } catch {
    return { kind: 'raw', text: query };
  }

  if (!isRecord(parsed)) {
    return { kind: 'raw', text: query };
  }

  const parameters: ConsultedParameter[] = Object.entries(parsed).map(([name, value]) =>
    name === 'unknownParameters' && typeof value === 'number'
      ? {
          label: 'Autres paramètres',
          value: `${String(value)} non conservé${value > 1 ? 's' : ''} (ni nom ni valeur)`,
        }
      : { label: QUERY_PARAMETER_LABELS[name] ?? name, value: describeParameterValue(value) },
  );

  return parameters.length === 0 ? { kind: 'none' } : { kind: 'parameters', parameters };
};

/**
 * Metadonnees rendues en JSON lisible : la requete d'une consultation, ecrite
 * comme une chaine JSON parce que le journal ne porte que des scalaires, est
 * deployee en objet. Rien d'autre n'est modifie, valeurs `[redacted]` comprises.
 */
export const readableMetadata = (metadata: Readonly<Record<string, unknown>>): Record<string, unknown> => {
  const query = metadata.query;

  if (typeof query !== 'string') {
    return { ...metadata };
  }

  try {
    const parsed: unknown = JSON.parse(query);

    return isRecord(parsed) ? { ...metadata, query: parsed } : { ...metadata };
  } catch {
    return { ...metadata };
  }
};

// ---------------------------------------------------------------------------
// Dates et pagination
// ---------------------------------------------------------------------------

const COMPACT_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const FULL_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'medium' });

const TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const formatWith = (format: Intl.DateTimeFormat, iso: string): string | null => {
  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? null : format.format(date);
};

/** « 4 oct., 10:42 » : la colonne « Quand » du tableau. */
export const formatCompact = (iso: string): string | null => formatWith(COMPACT_FORMAT, iso);

/** « 4 octobre 2026 à 10:42:07 » : le tiroir, a la seconde — c'est la granularite du journal. */
export const formatFull = (iso: string): string | null => formatWith(FULL_FORMAT, iso);

export const formatTime = (iso: string): string | null => formatWith(TIME_FORMAT, iso);

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

const eventCount = (total: number): string => {
  if (total === 0) {
    return 'Aucun événement';
  }

  return total === 1 ? '1 événement' : `${NUMBER_FORMAT.format(total)} événements`;
};

/**
 * Phrase de pagination. Le total est **celui du serveur** (`COUNT(*)`, filtres
 * compris) ; quand il manque, l'ecran le dit au lieu d'extrapoler.
 */
export const describeAuditPage = (state: PageState): string => {
  if (!totalIsKnown(state.total)) {
    return `Page ${NUMBER_FORMAT.format(state.page)} · ${eventCount(state.itemCount)} affiché${state.itemCount > 1 ? 's' : ''} · nombre total non communiqué par le serveur`;
  }

  return `${eventCount(state.total)} · page ${NUMBER_FORMAT.format(state.page)} sur ${NUMBER_FORMAT.format(pageCount(state))}`;
};
